/**
 * VOProformaPDF.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Generates a GCC Variation Order Proforma PDF (W3 format) in a new browser tab.
 *
 * Usage:
 *   import { generateVOProformaPdf } from "./VOProformaPDF";
 *
 *   generateVOProformaPdf({
 *     variation,        // The VO object from your variations store
 *     projectDetails,   // { client, department, contractNumber, contractDescription, contractor }
 *     certificateNo,    // Optional — payment certificate reference
 *   });
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { openHtmlWindow } from "../utils/printDoc";

// ─── Tiny formatting helpers (no external deps) ───────────────────────────────

const _n = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const _r = (v) => Math.round(_n(v) * 100) / 100;

const _fmt = (v) =>
  new Intl.NumberFormat("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(_n(v));

const _esc = (v) =>
  String(v || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const _fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "2-digit", year: "numeric",
  }).format(d).replace(/\//g, "/");
};

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * @param {object} params
 * @param {object} params.variation       - The approved VO record
 * @param {object} [params.projectDetails={}] - Project / contract metadata
 * @param {string} [params.certificateNo=""]  - Payment certificate reference
 */
export function generateVOProformaPdf({ variation = {}, projectDetails = {}, certificateNo = "" }) {
  // ── Pull all fields from the VO record ──────────────────────────────────────
  const voNo          = String(variation.variationNo || variation.voNo || "—");
  const voTitle       = String(variation.title || variation.voDescription || "");
  const voDescription = String(variation.description || variation.voDescription || voTitle || "");
  const voDate        = _fmtDate(variation.approvedDate || variation.date || variation.createdAt || "");
  const voStatus      = String(variation.status || "Approved").toUpperCase();
  const voReference   = String(variation.reference || variation.instructionRef || "");

  // Amounts
  const additionalWork = _r(variation.additionalWorkAmount ?? 0);
  const existingRates  = _r(variation.existingRatesAmount ?? 0);
  const omittedWork    = _r(variation.omittedWorkAmount ?? 0);

  // Gross = paymentCertificateVoAmount or grossVoAmount or sum of parts
  const grossVoAmount = _r(
    variation.paymentCertificateVoAmount ??
    variation.grossVoAmount ??
    (additionalWork + existingRates)
  );

  // Net proforma = gross − omissions
  const netVoAmount = _r(
    variation.netVoAmount ??
    variation.proformaNetVoAmount ??
    (grossVoAmount - omittedWork)
  );

  // Quantity / rate
  const approvedQty  = _n(variation.approvedQty ?? variation.approvedQuantity ?? variation.quantity ?? 1) || 1;
  const rate         = _n(variation.rate ?? variation.approvedRate ?? variation.tenderRate ?? 0) ||
                       (approvedQty > 0 ? _r(grossVoAmount / approvedQty) : grossVoAmount);

  // Line items for additional work
  const additionalItems = Array.isArray(variation.additionalWorkItems)
    ? variation.additionalWorkItems
    : [];

  // Line items for existing-rates work
  const existingRateItems = Array.isArray(variation.existingRateItems)
    ? variation.existingRateItems
    : [];

  // Line items for omitted SOQ
  const omittedItems = Array.isArray(variation.omittedItems || variation.omissionItems)
    ? (variation.omittedItems || variation.omissionItems)
    : [];

  // ── Project / contract metadata ─────────────────────────────────────────────
  const client       = projectDetails.client || projectDetails.employer || projectDetails.owner || "";
  const department   = projectDetails.department || "";
  const contractNo   = projectDetails.contractNumber || projectDetails.contractNo || "";
  const contractDesc = projectDetails.contractDescription || projectDetails.description || projectDetails.title || projectDetails.name || "";
  const contractor   = projectDetails.contractor || variation.contractor || "";

  // ── Helper to build a line-items table ─────────────────────────────────────
  const buildItemRows = (items = [], fallbackQty, fallbackRate, fallbackAmount, fallbackDesc) => {
    if (items.length > 0) {
      return items.map((item, i) => {
        // Omission entries are stored as omitQty / tenderRate / omittedAmount
        const qty    = _n(item.quantity ?? item.qty ?? item.omitQty ?? 0);
        const r      = _n(item.rate ?? item.unitRate ?? item.tenderRate ?? 0);
        const amt    = _r(item.amount ?? item.totalAmount ?? item.omittedAmount ?? qty * r);
        const unit   = _esc(item.unit || "");
        const desc   = _esc(item.description || item.itemDescription || `Item ${i + 1}`);
        const itemNo = _esc(item.itemNo || item.soqItemNo || "");
        return `
          <tr>
            <td class="center">${_esc(String(i + 1))}</td>
            <td class="center">${itemNo}</td>
            <td>${desc}</td>
            <td class="center">${unit}</td>
            <td class="right">${qty > 0 ? _fmt(qty) : "—"}</td>
            <td class="right">${r > 0 ? _fmt(r) : "—"}</td>
            <td class="right bold">${_fmt(amt)}</td>
          </tr>`;
      }).join("");
    }
    // Single summary row when no line-item breakdown is available
    return `
      <tr>
        <td class="center">1</td>
        <td class="center">—</td>
        <td>${_esc(fallbackDesc)}</td>
        <td class="center">Sum</td>
        <td class="right">${_fmt(fallbackQty)}</td>
        <td class="right">${_fmt(fallbackRate)}</td>
        <td class="right bold">${_fmt(fallbackAmount)}</td>
      </tr>`;
  };

  // ── Instruction / justification text ───────────────────────────────────────
  const instruction = _esc(variation.instruction || variation.scopeOfWork || variation.justification || "Refer to attached instruction.");
  const motivation  = _esc(variation.motivation || variation.reason || variation.reasonForVariation || "");

  // ── Build HTML ──────────────────────────────────────────────────────────────
  const additionalRows   = buildItemRows(additionalItems, approvedQty, rate, additionalWork, voTitle || "Additional Work");
  const existingRateRows = buildItemRows(existingRateItems, approvedQty, rate, existingRates, voTitle || "Work at Existing Rates");
  const omissionRows     = omittedWork > 0
    ? buildItemRows(omittedItems, approvedQty, 0, omittedWork, "Omitted Work (SOQ deduction)")
    : `<tr><td colspan="7" class="center" style="color:#64748B;font-style:italic">No omissions recorded for this VO</td></tr>`;

  const html = `<!doctype html><html><head>
<meta charset="utf-8">
<title>VO Proforma — ${_esc(voNo)}</title>
<style>
@page { size: A4 portrait; margin: 10mm 12mm; }
* { box-sizing: border-box; }
html, body { width: 210mm; margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; font-size: 10.5px; color: #111827; line-height: 1.25; }
.sheet { width: 100%; }

/* ── Header ── */
.header-grid { display: grid; grid-template-columns: 1fr 200px 30px; gap: 8px; align-items: start; margin-bottom: 6px; }
.top-lines { font-size: 10px; line-height: 1.3; text-transform: uppercase; font-weight: 700; }
.vo-meta { font-size: 10px; }
.vo-meta .row { display: grid; grid-template-columns: 1fr 100px; gap: 6px; align-items: center; margin-bottom: 2px; }
.vo-number { font-size: 11.5px; font-weight: 700; text-align: right; text-transform: uppercase; margin-bottom: 6px; }
.sheet-ref { font-weight: 700; text-align: right; font-size: 11px; }
.title-bar { text-align: center; font-size: 12px; font-weight: 700; margin: 4px 0 6px; padding: 4px 6px; text-transform: uppercase; border: 2px solid #111827; }
.subtitle-bar { text-align: center; font-size: 10px; font-weight: 700; margin: 0 0 5px; padding: 3px 6px; text-transform: uppercase; border: 1px solid #111827; background: #f3f4f6; }
.vo-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #111827; margin-bottom: 5px; font-size: 10px; }
.vo-info-cell { padding: 3px 7px; border-right: 1px solid #111827; border-bottom: 1px solid #111827; }
.vo-info-cell:nth-child(even) { border-right: none; }
.vo-info-cell .label { font-weight: 700; text-transform: uppercase; font-size: 9px; color: #374151; }
.vo-info-cell .val { font-weight: 700; margin-top: 1px; }
.instruction-box { border: 1px solid #111827; padding: 5px 8px; margin-bottom: 5px; font-size: 10px; }
.instruction-box .label { font-weight: 700; text-transform: uppercase; font-size: 9px; color: #374151; margin-bottom: 3px; }

/* ── Tables ── */
table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-bottom: 5px; }
th, td { border: 1px solid #111827; padding: 3px 5px; vertical-align: middle; }
th { background: #f3f4f6; font-weight: 700; text-align: center; font-size: 9.5px; text-transform: uppercase; }
.w-no { width: 5%; }
.w-soq { width: 10%; }
.w-desc { width: 38%; }
.w-unit { width: 7%; }
.w-qty { width: 10%; }
.w-rate { width: 13%; }
.w-amt { width: 17%; }
.center { text-align: center; }
.right { text-align: right; white-space: nowrap; }
.bold { font-weight: 700; }
.subtotal-row td { background: #f9fafb; font-weight: 700; }
.total-row td { background: #e5f5f0; font-weight: 700; font-size: 11px; }
.omission-row td { background: #fbe9e8; }
.section-label { font-size: 10px; font-weight: 700; text-transform: uppercase; margin: 5px 0 2px; color: #374151; letter-spacing: 0.04em; }

/* ── Summary strip ── */
.summary-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border: 2px solid #111827; margin-bottom: 5px; }
.summary-cell { padding: 4px 7px; border-right: 1px solid #111827; text-align: right; }
.summary-cell:last-child { border-right: none; }
.summary-cell .s-label { font-size: 8.5px; font-weight: 700; text-transform: uppercase; color: #374151; }
.summary-cell .s-val { font-size: 12px; font-weight: 700; margin-top: 2px; }
.summary-cell.highlight .s-val { color: #0E8C6A; }

/* ── Signatures ── */
.signatures { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0; margin-top: 5px; border: 2px solid #111827; page-break-inside: avoid; }
.sig-box { min-height: 90px; padding: 5px 8px; border-right: 1px solid #111827; display: flex; flex-direction: column; justify-content: space-between; font-size: 9px; }
.sig-box:last-child { border-right: none; }
.sig-title { font-weight: 700; text-transform: uppercase; font-size: 9px; margin-bottom: 4px; border-bottom: 1px solid #d1d5db; padding-bottom: 2px; }
.sig-line { border-top: 1px solid #111827; padding-top: 2px; font-weight: 700; text-transform: uppercase; text-align: center; margin-top: 12px; font-size: 8.5px; }
.date-line { border-top: 1px solid #111827; padding-top: 2px; text-align: center; margin-top: 6px; font-size: 8.5px; }

/* ── Footer ── */
.footer-meta { margin-top: 4px; display: flex; justify-content: space-between; font-size: 8.5px; color: #374151; }

/* ── Print ── */
.print-actions { margin: 0 0 10px; padding: 8px 12px; background: #f9fafb; border: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 8px; }
.print-actions button { padding: 7px 14px; border: none; border-radius: 6px; font-weight: 700; cursor: pointer; font-size: 12px; }
.btn-print { background: #2563eb; color: white; }
.btn-close { background: #6b7280; color: white; }
@media print {
  .print-actions { display: none; }
  html, body { width: 210mm; overflow: hidden; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
</style>
</head>
<body>

<div class="print-actions">
  <button class="btn-print" onclick="window.print()">🖨 Print / Save as PDF</button>
  <button class="btn-close" onclick="window.close()">Close</button>
</div>

<div class="sheet">

  <!-- ── HEADER ─────────────────────────────────────────────── -->
  <div class="header-grid">
    <div class="top-lines">
      <div>${_esc(client)}</div>
      ${department ? `<div>${_esc(department)}</div>` : ""}
      <div>${_esc(contractNo)}</div>
      <div>${_esc(contractDesc)}</div>
      ${contractor ? `<div>Contractor: ${_esc(contractor)}</div>` : ""}
    </div>
    <div class="vo-meta">
      <div class="vo-number">VARIATION ORDER NO.: ${_esc(voNo)}</div>
      ${certificateNo ? `<div class="row"><div>Certificate No:</div><strong>${_esc(certificateNo)}</strong></div>` : ""}
      <div class="row"><div>Date:</div><strong>${_esc(voDate)}</strong></div>
      <div class="row"><div>Status:</div><strong>${_esc(voStatus)}</strong></div>
      ${voReference ? `<div class="row"><div>Ref:</div><strong>${_esc(voReference)}</strong></div>` : ""}
    </div>
    <div class="sheet-ref">W3</div>
  </div>

  <div class="title-bar">VARIATION ORDER PROFORMA — SUMMARY OF EXTRA WORK</div>

  <!-- ── VO INFO GRID ────────────────────────────────────────── -->
  <div class="vo-info-grid">
    <div class="vo-info-cell"><div class="label">VO Number</div><div class="val">${_esc(voNo)}</div></div>
    <div class="vo-info-cell"><div class="label">Date</div><div class="val">${_esc(voDate)}</div></div>
    <div class="vo-info-cell" style="grid-column:1/-1;border-right:none"><div class="label">VO Title / Description</div><div class="val">${_esc(voTitle || voDescription)}</div></div>
    ${voReference ? `<div class="vo-info-cell" style="grid-column:1/-1;border-right:none"><div class="label">Instruction Reference</div><div class="val">${_esc(voReference)}</div></div>` : ""}
  </div>

  <!-- ── INSTRUCTION ────────────────────────────────────────── -->
  <div class="instruction-box">
    <div class="label">Scope of Work / Instruction</div>
    <div>${instruction}</div>
    ${motivation ? `<div style="margin-top:4px"><span class="label">Motivation / Reason: </span>${motivation}</div>` : ""}
  </div>

  <!-- ── SECTION A: ADDITIONAL WORK AT NEW RATES ──────────── -->
  ${additionalWork > 0 ? `
  <div class="section-label">A. Additional Work at New Rates</div>
  <table>
    <thead>
      <tr>
        <th class="w-no">No.</th>
        <th class="w-soq">SOQ Item</th>
        <th class="w-desc">Description</th>
        <th class="w-unit">Unit</th>
        <th class="w-qty">Quantity</th>
        <th class="w-rate">Rate (ZAR)</th>
        <th class="w-amt">Amount (ZAR)</th>
      </tr>
    </thead>
    <tbody>
      ${additionalRows}
      <tr class="subtotal-row">
        <td colspan="6" class="right">Subtotal — Additional Work at New Rates</td>
        <td class="right">${_fmt(additionalWork)}</td>
      </tr>
    </tbody>
  </table>
  ` : ""}

  <!-- ── SECTION B: WORK AT EXISTING RATES ─────────────────── -->
  ${existingRates > 0 ? `
  <div class="section-label">B. Work at Existing SOQ Rates</div>
  <table>
    <thead>
      <tr>
        <th class="w-no">No.</th>
        <th class="w-soq">SOQ Item</th>
        <th class="w-desc">Description</th>
        <th class="w-unit">Unit</th>
        <th class="w-qty">Quantity</th>
        <th class="w-rate">Rate (ZAR)</th>
        <th class="w-amt">Amount (ZAR)</th>
      </tr>
    </thead>
    <tbody>
      ${existingRateRows}
      <tr class="subtotal-row">
        <td colspan="6" class="right">Subtotal — Work at Existing Rates</td>
        <td class="right">${_fmt(existingRates)}</td>
      </tr>
    </tbody>
  </table>
  ` : ""}

  <!-- ── SECTION C: OMISSIONS ───────────────────────────────── -->
  <div class="section-label">C. Omitted Work (SOQ Deductions)</div>
  <table>
    <thead>
      <tr>
        <th class="w-no">No.</th>
        <th class="w-soq">SOQ Item</th>
        <th class="w-desc">Description</th>
        <th class="w-unit">Unit</th>
        <th class="w-qty">Quantity</th>
        <th class="w-rate">Rate (ZAR)</th>
        <th class="w-amt">Amount (ZAR)</th>
      </tr>
    </thead>
    <tbody class="omission-row">
      ${omissionRows}
      ${omittedWork > 0 ? `
      <tr class="subtotal-row">
        <td colspan="6" class="right">Subtotal — Omissions (deduct)</td>
        <td class="right">(${_fmt(omittedWork)})</td>
      </tr>` : ""}
    </tbody>
  </table>

  <!-- ── SUMMARY STRIP ─────────────────────────────────────── -->
  <div class="summary-strip">
    <div class="summary-cell">
      <div class="s-label">Additional Work (A)</div>
      <div class="s-val">${_fmt(additionalWork)}</div>
    </div>
    <div class="summary-cell">
      <div class="s-label">Existing Rates (B)</div>
      <div class="s-val">${_fmt(existingRates)}</div>
    </div>
    <div class="summary-cell">
      <div class="s-label">Omissions (C)</div>
      <div class="s-val">(${_fmt(omittedWork)})</div>
    </div>
    <div class="summary-cell highlight">
      <div class="s-label">NET VO AMOUNT</div>
      <div class="s-val">${_fmt(netVoAmount)}</div>
    </div>
  </div>

  <!-- ── GROSS TOTAL ROW ────────────────────────────────────── -->
  <table style="margin-bottom:5px">
    <tbody>
      <tr class="total-row">
        <td colspan="6" class="right" style="font-size:10.5px">GROSS VARIATION ORDER AMOUNT (excl. omissions, excl. VAT)</td>
        <td class="right" style="width:17%">${_fmt(grossVoAmount)}</td>
      </tr>
      <tr class="total-row">
        <td colspan="6" class="right" style="font-size:10.5px">NET VARIATION ORDER AMOUNT (after deducting omissions, excl. VAT)</td>
        <td class="right" style="width:17%">${_fmt(netVoAmount)}</td>
      </tr>
    </tbody>
  </table>

  <!-- ── SIGNATURE PANEL ───────────────────────────────────── -->
  <div class="signatures">
    <div class="sig-box">
      <div>
        <div class="sig-title">Prepared by (Contractor)</div>
        <p style="margin:0;font-size:9px">I certify that the quantities and amounts stated above are correct in accordance with the provisions of the contract.</p>
      </div>
      <div>
        <div class="sig-line">CONTRACTOR'S REPRESENTATIVE</div>
        <div class="date-line">Date</div>
      </div>
    </div>
    <div class="sig-box">
      <div>
        <div class="sig-title">Checked by (Employer's Agent)</div>
        <p style="margin:0;font-size:9px">Issued in terms of section 6.10 of the General Conditions of Contract and certified to be correct.</p>
      </div>
      <div>
        <div class="sig-line">EMPLOYER'S AGENT</div>
        <div class="date-line">Date</div>
      </div>
    </div>
    <div class="sig-box">
      <div>
        <div class="sig-title">Approved by (Employer's Representative)</div>
        <p style="margin:0;font-size:9px">Approved in terms of clause 6.10.4 of the General Conditions of Contract.</p>
      </div>
      <div>
        <div class="sig-line">EMPLOYER'S REPRESENTATIVE</div>
        <div class="date-line">Date received</div>
      </div>
    </div>
  </div>

  <div class="footer-meta">
    <div>VO: ${_esc(voNo)}${certificateNo ? ` · Certificate: ${_esc(certificateNo)}` : ""}</div>
    <div>${_esc(contractNo)}</div>
    <div>Generated: ${new Date().toLocaleDateString("en-ZA")}</div>
  </div>

</div><!-- /sheet -->
</body></html>`;

  // ── Open the PDF in a new tab ──────────────────────────────────────────────
  openHtmlWindow(html, { features: "width=900,height=1100" });
}